/**
 * The client half of the ghost relay.
 *
 * Hard rule: **this never throws into the game loop.** Every socket failure
 * becomes a `status` event and an automatic reconnect. With the server down the
 * game is a perfectly good single-player climb; the only difference is an amber
 * connection dot.
 */

import {
  PROTOCOL_VERSION,
  roundState,
  type C2S,
  type PlayerInfo,
  type PlayerState,
  type S2C,
  type S2CSnap,
  type S2CWelcome,
} from '@shared/protocol';
import { CLIENT_SEND_HZ, RECONNECT_BACKOFF_MS } from '@shared/constants';

/** Connection state, for the HUD dot. */
export type NetStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

/** Event name → payload. */
export interface NetEvents {
  /** Handshake accepted; carries your id and everyone already in the room. */
  welcome: S2CWelcome;
  /** Somebody joined. */
  joined: PlayerInfo;
  /** Somebody left; payload is their id. */
  left: number;
  /** A snapshot of everyone whose state changed. */
  snap: S2CSnap;
  /** Connection state changed. */
  status: NetStatus;
}

type Listener<K extends keyof NetEvents> = (payload: NetEvents[K]) => void;

/** Forced keyframe interval — a ghost that stops moving still refreshes this often. */
const KEYFRAME_MS = 1000;
/** Movement thresholds for "is this update worth a packet?". */
const POS_EPS = 0.02;
const YAW_EPS = 0.01;
const CHARGE_EPS = 0.05;
/** Reconnect jitter, as a fraction of the backoff delay. */
const JITTER = 0.2;
/** Close codes in the 4000 range mean "the server rejected you"; do not retry blindly. */
const FATAL_CLOSE_CODES = new Set([4001, 4002, 4003]);

function wsUrl(): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.host}/ws`;
}

export class NetClient {
  private ws: WebSocket | null = null;
  private _status: NetStatus = 'offline';
  private _selfId: number | null = null;

  private name = '';
  private color: number | undefined;
  private wantConnection = false;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set when the server rejected us for a reason retrying will not fix. */
  private fatal = false;

  private lastSentAt = 0;
  private lastKeyframeAt = 0;
  private lastSent: PlayerState | null = null;

  private readonly listeners = new Map<keyof NetEvents, Set<Listener<never>>>();

  /** Current connection state. */
  get status(): NetStatus {
    return this._status;
  }

  /** Your player id once `welcome` has arrived, else `null`. */
  get selfId(): number | null {
    return this._selfId;
  }

  /** True while a socket is open and the handshake has completed. */
  get online(): boolean {
    return this._status === 'connected';
  }

  // -------------------------------------------------------------------------
  // events
  // -------------------------------------------------------------------------

  /** Subscribe. Returns an unsubscribe function. */
  on<K extends keyof NetEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (set === undefined) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn as Listener<never>);
    return () => this.off(event, fn);
  }

  /** Unsubscribe a listener registered with {@link on}. */
  off<K extends keyof NetEvents>(event: K, fn: Listener<K>): void {
    this.listeners.get(event)?.delete(fn as Listener<never>);
  }

  private emit<K extends keyof NetEvents>(event: K, payload: NetEvents[K]): void {
    const set = this.listeners.get(event);
    if (set === undefined) return;
    for (const fn of set) {
      try {
        (fn as Listener<K>)(payload);
      } catch (err) {
        // A broken listener must not take the socket (or the frame) down.
        console.error('[net] listener threw', err);
      }
    }
  }

  private setStatus(next: NetStatus): void {
    if (this._status === next) return;
    this._status = next;
    this.emit('status', next);
  }

  // -------------------------------------------------------------------------
  // lifecycle
  // -------------------------------------------------------------------------

  /**
   * Connect (or reconnect with a new identity). Safe to call repeatedly; the
   * latest name/colour is what gets re-sent on every future reconnect.
   */
  connect(name: string, color?: number): void {
    this.name = name;
    this.color = color;
    this.wantConnection = true;
    this.fatal = false;
    this.attempt = 0;
    this.clearRetry();
    if (this.ws !== null) {
      // Already have (or are opening) a socket — re-handshake in place.
      if (this.ws.readyState === WebSocket.OPEN) {
        this.sendJoin();
        return;
      }
      if (this.ws.readyState === WebSocket.CONNECTING) return;
    }
    this.open();
  }

  /** Close the socket and stop reconnecting. */
  disconnect(): void {
    this.wantConnection = false;
    this.clearRetry();
    this._selfId = null;
    this.lastSent = null;
    const ws = this.ws;
    this.ws = null;
    if (ws !== null) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close(1000, 'client disconnect');
      } catch {
        /* already gone */
      }
    }
    this.setStatus('offline');
  }

  private open(): void {
    if (!this.wantConnection || this.fatal) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl());
    } catch (err) {
      console.warn('[net] could not open socket', err);
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.lastSent = null;
      this.sendJoin();
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      if (typeof ev.data !== 'string') return;
      this.handle(ev.data);
    };
    ws.onerror = () => {
      // `close` always follows; nothing to do but avoid an unhandled error event.
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this._selfId = null;
      this.lastSent = null;
      if (FATAL_CLOSE_CODES.has(ev.code)) {
        this.fatal = true;
        this.wantConnection = false;
        this.setStatus('offline');
        return;
      }
      if (!this.wantConnection) {
        this.setStatus('offline');
        return;
      }
      this.setStatus('reconnecting');
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (!this.wantConnection || this.fatal || this.retryTimer !== null) return;
    const idx = Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1);
    const base = RECONNECT_BACKOFF_MS[idx] ?? 8000;
    const delay = base * (1 + (Math.random() * 2 - 1) * JITTER);
    this.attempt++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // sending
  // -------------------------------------------------------------------------

  private send(msg: C2S): void {
    const ws = this.ws;
    if (ws === null || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch (err) {
      console.warn('[net] send failed', err);
    }
  }

  private sendJoin(): void {
    this.send(
      this.color === undefined
        ? { t: 'join', v: PROTOCOL_VERSION, name: this.name }
        : { t: 'join', v: PROTOCOL_VERSION, name: this.name, color: this.color },
    );
  }

  /**
   * Offer the local player's state to the network. Call this every frame — it
   * is rate-limited to `CLIENT_SEND_HZ` and additionally suppressed when nothing
   * meaningful changed, with a forced keyframe once a second.
   */
  sendState(s: PlayerState): void {
    if (this._selfId === null) return;
    const now = performance.now();
    if (now - this.lastSentAt < 1000 / CLIENT_SEND_HZ) return;

    const rounded = roundState(s);
    const keyframe = now - this.lastKeyframeAt >= KEYFRAME_MS;
    if (!keyframe && !this.worthSending(rounded)) return;

    this.lastSentAt = now;
    if (keyframe) this.lastKeyframeAt = now;
    this.lastSent = rounded;
    this.send({ t: 'state', s: rounded });
  }

  private worthSending(s: PlayerState): boolean {
    const prev = this.lastSent;
    if (prev === null) return true;
    if (prev.a !== s.a) return true;
    if (Math.abs(prev.c - s.c) > CHARGE_EPS) return true;
    if (Math.abs(prev.y - s.y) > YAW_EPS) return true;
    const dx = prev.p[0] - s.p[0];
    const dy = prev.p[1] - s.p[1];
    const dz = prev.p[2] - s.p[2];
    if (dx * dx + dy * dy + dz * dz > POS_EPS * POS_EPS) return true;
    const dlx = prev.l[0] - s.l[0];
    const dlz = prev.l[1] - s.l[1];
    if (dlx * dlx + dlz * dlz > YAW_EPS * YAW_EPS) return true;
    return false;
  }

  // -------------------------------------------------------------------------
  // receiving
  // -------------------------------------------------------------------------

  private handle(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (typeof parsed !== 'object' || parsed === null) return;
    const msg = parsed as S2C;

    switch (msg.t) {
      case 'welcome':
        this._selfId = msg.id;
        this.lastKeyframeAt = 0;
        this.setStatus('connected');
        this.emit('welcome', msg);
        break;
      case 'joined':
        this.emit('joined', msg.player);
        break;
      case 'left':
        this.emit('left', msg.id);
        break;
      case 'snap':
        this.emit('snap', msg);
        break;
      case 'ping':
        this.send({ t: 'pong', ts: msg.ts });
        break;
      case 'error':
        console.warn(`[net] server rejected us (${msg.code}): ${msg.msg}`);
        // The close that follows carries a 4000-range code and stops retries.
        break;
      default:
        break;
    }
  }
}
